import React, { useCallback, useRef } from 'react';
import { BlockRenderer } from '../renderer';
import {
  createBlock,
  flattenNavigableBlocks,
  insertBlockAfter,
  removeBlockFromTree,
  updateBlockInTree,
} from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { EDITABLE_TYPES, NAVIGABLE_TYPES } from '../utils/editorConstants';
import { CELL_SLASH_EXCLUDE, emptyCellBlocks } from '../utils/tableCellBlocks';
import useBlockMerge from '../hooks/useBlockHandlers/hooks/useBlockMerge';

/**
 * Mini block list inside one table cell. Slash / typing stay in this cell;
 * page DnD is wired through BlockRenderer so blocks can move in and out.
 */
export const TableCellCanvas = ({
  blocks,
  onChange,
  tableId,
  rowIndex,
  colIndex,
  onUpdate: _pageUpdate,
  onInsertAfter: _pageInsert,
  onUpdateAndInsert: _pageUpdateInsert,
  onDelete: _pageDelete,
  onMergeWithPrevious: _pageMergePrev,
  onMergeWithNext: _pageMergeNext,
  onNavigate: _pageNavigate,
  onEscapeContainer: _pageEscape,
  onWrapInCallout: _pageWrap,
  onUnwrapCallout: _pageUnwrap,
  ...editorProps
}) => {
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const commit = useCallback(
    (next) => {
      const tree = next?.length ? next : emptyCellBlocks();
      blocksRef.current = tree;
      onChange(tree);
    },
    [onChange]
  );

  const { handleMergeWithPrevious, handleMergeWithNext } = useBlockMerge({
    blocksRef,
    commit,
  });

  const handleUpdate = useCallback(
    (blockId, updates) => {
      commit(updateBlockInTree(blocksRef.current, blockId, updates));
    },
    [commit]
  );

  const handleInsertAfter = useCallback(
    (targetId, typeOrBlock = 'text') => {
      const newBlock = typeof typeOrBlock === 'string' ? createBlock(typeOrBlock) : typeOrBlock;
      commit(insertBlockAfter(blocksRef.current, targetId, newBlock));
      if (EDITABLE_TYPES.includes(newBlock.type)) focusEditable(newBlock.id);
    },
    [commit]
  );

  const handleUpdateAndInsert = useCallback(
    (blockId, updates, newBlock, { focusNewBlock = true } = {}) => {
      let tree = updateBlockInTree(blocksRef.current, blockId, updates);
      tree = insertBlockAfter(tree, blockId, newBlock);
      commit(tree);
      if (focusNewBlock && EDITABLE_TYPES.includes(newBlock.type)) {
        focusEditable(newBlock.id);
      }
    },
    [commit]
  );

  const handleDelete = useCallback(
    (blockId) => {
      commit(removeBlockFromTree(blocksRef.current, blockId));
    },
    [commit]
  );

  const handleNavigate = useCallback((blockId, direction) => {
    const list = flattenNavigableBlocks(blocksRef.current, NAVIGABLE_TYPES);
    const index = list.findIndex((b) => b.id === blockId);
    if (index < 0) return;
    const next =
      direction === 'up' || direction === 'left' ? list[index - 1] : list[index + 1];
    if (!next) return;
    focusEditable(next.id, {
      atStart: direction === 'down' || direction === 'right',
    });
  }, []);

  return (
    <div
      className={`table-cell-canvas${rowIndex === -1 ? ' table-cell-canvas--header' : ''}`}
      data-table-cell={`${tableId}-${rowIndex}-${colIndex}`}
    >
      {blocks.map((block, i) => (
        <BlockRenderer
          key={block.id}
          block={block}
          {...editorProps}
          prevType={blocks[i - 1]?.type}
          nextType={blocks[i + 1]?.type}
          onUpdate={handleUpdate}
          onInsertAfter={handleInsertAfter}
          onUpdateAndInsert={handleUpdateAndInsert}
          onDelete={handleDelete}
          onMergeWithPrevious={handleMergeWithPrevious}
          onMergeWithNext={handleMergeWithNext}
          onNavigate={handleNavigate}
          onWrapInCallout={undefined}
          onUnwrapCallout={undefined}
          slashExcludeIds={CELL_SLASH_EXCLUDE}
          compact
          inTableCell
        />
      ))}
    </div>
  );
};
